import { bulkSubscribe, modelState, ModelStateList } from "@/utils/modelState";
import { ReservationPayment, ReservationState } from "./types";

export const ReservationStatus = Symbol("ReservationStatus");
export interface ReservationStatus {
    [ReservationStatus]: boolean;
}

export const ReservationQuoteStatus = Symbol("ReservationQuoteStatus");
export interface ReservationQuoteStatus extends ReservationStatus {
    [ReservationQuoteStatus]: boolean;
}

export function isReservationQuoteStatus(val: ReservationStatus): val is ReservationQuoteStatus {
    return val && (val as ReservationQuoteStatus)[ReservationQuoteStatus] === true;
}

export const ReservationReservedStatus = Symbol("ReservationReservedStatus");
export interface ReservationReservedStatus extends ReservationStatus {
    [ReservationReservedStatus]: boolean;
}

export function isReservationReservedStatus(val: ReservationStatus): val is ReservationReservedStatus {
    return val && (val as ReservationReservedStatus)[ReservationReservedStatus] === true;
}

export const ReservationReleasedStatus = Symbol("ReservationReleasedStatus");
export interface ReservationReleasedStatus extends ReservationStatus {
    [ReservationReleasedStatus]: boolean;
    Credits: ModelStateList<ReservationReleasedStatusCredit>;
}

export interface ReservationReleasedStatusCredit {
    Payment: ReservationPayment;
    Amount: modelState<number>;
}

export function isReservationReleasedStatus(val: ReservationStatus): val is ReservationReleasedStatus {
    return val && (val as ReservationReleasedStatus)[ReservationReleasedStatus] === true;
}

export interface ReservationStatusDto {
    $type: string;
    value: ReservationStatusValueDto;
}

export interface ReservationStatusValueDto {
    //
}

export interface ReservationQuoteStatusValueDto extends ReservationStatusValueDto {
    //
}

export interface ReservationReservedStatusValueDto extends ReservationStatusValueDto {
    //
}

export interface ReservationReleasedStatusValueDto extends ReservationStatusValueDto {
    credits: ReservationReleasedStatusCreditDto[];
}

export interface ReservationReleasedStatusCreditDto {
    paymentId: string;
    amount: number;
}

export function serializeReservationStatus(action: ReservationStatus): ReservationStatusDto {
    if (!action) {
        return null;
    }

    if (isReservationQuoteStatus(action)) {
        return {
            $type: "Quote",
            value: {}
        };
    }

    if (isReservationReservedStatus(action)) {
        return {
            $type: "Reserved",
            value: {}
        };
    }

    if (isReservationReleasedStatus(action)) {
        return {
            $type: "Released",
            value: {}
        };
    }

    throw new Error("Invalid status type");
}

export function deserializeReservationStatus(reservationState: ReservationState, dto: ReservationStatusDto): ReservationStatus {
    if (!dto) {
        return null;
    }

    switch (dto.$type) {
        case "Quote":
            return createReservationQuoteStatus();
        case "Reserved":
            return createReservationReservedStatus();
        case "Released":
            const releasedDto = dto.value as ReservationReleasedStatusValueDto;

            const releasedStatus = createReservationReleasedStatus();

            (releasedDto.credits ?? []).forEach(creditDto => {
                const credit: ReservationReleasedStatusCredit = {
                    Payment: reservationState.Payments.toArray().find(p => p.PaymentId === creditDto.paymentId),
                    Amount: modelState(creditDto.amount)
                };
                releasedStatus.Credits.add(credit);
            });

            return releasedStatus;
        default:
            throw new Error("Invalid status type");
    }
}

export function getReservationStatusName(status: ReservationStatus): string {
    if (isReservationQuoteStatus(status)) {
        return "Quote";
    }

    if (isReservationReservedStatus(status)) {
        return "Reserved";
    }

    if (isReservationReleasedStatus(status)) {
        return "Released";
    }

    throw new Error("Invalid status type");
}

export function createReservationQuoteStatus(): ReservationQuoteStatus {
    const status: ReservationQuoteStatus = {
        [ReservationStatus]: true,
        [ReservationQuoteStatus]: true
    };

    return status;
};

export function createReservationReservedStatus(): ReservationReservedStatus {
    const status: ReservationReservedStatus = {
        [ReservationStatus]: true,
        [ReservationReservedStatus]: true
    };

    return status;
}

export function createReservationReleasedStatus(): ReservationReleasedStatus {
    const status: ReservationReleasedStatus = {
        [ReservationStatus]: true,
        [ReservationReleasedStatus]: true,
        Credits: new ModelStateList<ReservationReleasedStatusCredit>()
    };

    return status;
}

export function createReservationStatus(name: string): ReservationStatus {
    switch (name) {
        case "Quote":
            return createReservationQuoteStatus();
        case "Reserved":
            return createReservationReservedStatus();
        case "Released":
            return createReservationReleasedStatus();
        default:
            throw new Error("Invalid status name");
    }
}

export function hydrateReservationStatus(reservation: ReservationState) {

    const isDirty = () => {
        if (reservation.ReservationStatus.isDirty) {
            return true;
        }

        if (isReservationQuoteStatus(reservation.ReservationStatus.value)) {
            return isReservationQuoteStatusDirty(reservation.ReservationStatus.value);
        } else if (isReservationReleasedStatus(reservation.ReservationStatus.value)) {
            return isReservationReleasedStatusDirty(reservation.ReservationStatus.value);
        }

        return false;
    }

    const signal = () => {
        reservation.ReservationStatusIsDirty.setValue(isDirty());
        reservation.ReservationStatusVersion.setValue(reservation.ReservationStatusVersion.value + 1);
    };

    // Amount strategy
    let oldReservationStatusSubscription: () => void = null;

    function subscribeReservationStatus() {
        if (reservation.ReservationStatus.value) {
            const status = reservation.ReservationStatus.value;

            if (isReservationQuoteStatus(status)) {
                oldReservationStatusSubscription = hydrateReservationQuoteStatus(status, signal);
            } else if (isReservationReleasedStatus(status)) {
                oldReservationStatusSubscription = hydrateReservationReleasedStatus(reservation, status, signal);
            } else {
                oldReservationStatusSubscription = () => { };
            }
        }
    }

    subscribeReservationStatus();

    const unsubscribeReservationStatus = reservation.ReservationStatus.subscribe(() => {
        oldReservationStatusSubscription?.();

        subscribeReservationStatus();

        signal();
    });

    return unsubscribeReservationStatus;
}

function hydrateReservationQuoteStatus(status: ReservationQuoteStatus, signal: () => void) {
    return () => { };
}

function isReservationQuoteStatusDirty(status: ReservationQuoteStatus) {
    return false;
}

function hydrateReservationReleasedStatus(
    reservationState: ReservationState,
    status: ReservationReleasedStatus,
    signal: () => void) {
    const subscriptions = [
        status.Credits.subscribe(() => {
            signal();
        }),
        bulkSubscribe(
            status.Credits,
            [
                pb => pb.Amount
            ],
            () => signal()),
        reservationState.Payments.subscribe(changes => {
            // remove credits for payments that no longer exist
            // add payments that don't have credits
            if (changes.action === "add") {
                status.Credits.add({
                    Payment: changes.item,
                    Amount: modelState(0)
                });
            } else if (changes.action === "remove") {
                status.Credits.removeAt(changes.index);
            } else if (changes.action === "reset") {
                status.Credits.undo();
            }
        })
    ];

    const existingPayments = reservationState.Payments.toArray();

    existingPayments.forEach(payment => {
        if (!status.Credits.toArray().some(c => c.Payment === payment)) {
            status.Credits.add({
                Payment: payment,
                Amount: modelState(0)
            });
        }
    });

    return () => {
        subscriptions.forEach(sub => sub());
    };
}

function isReservationReleasedStatusDirty(status: ReservationReleasedStatus): boolean {
    return status.Credits.isDirty.value ||
        status.Credits.toArray().some(c => c.Amount.isDirty);
}
